import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import API from "../axios";

const Vehicles = () => {
    const params = new URLSearchParams(window.location.search);
    const [page, setPage] = useState<number>(Number(params.get("page")) || 1);
    const [odooId, setOdooId] = useState(params.get("odooId") || "");
    const [defaultPrice, setDefaultPrice] = useState(
        params.get("defaultPrice") || ""
    );
    const [active, setActive] = useState(params.get("active") || "");
    const [notAudited, setNotAudited] = useState(params.get("notAudited") || "");
    const [searchString, setSearchString] = useState(
        params.get("searchString") || ""
    );
    const [limit] = useState(10); // Should match backend default

    const [filters, setFilters] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState<Array<any>>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [syncLoading, setSyncLoading] = useState(false);

    const updateQueryParams = (updates: Record<string, string>) => {
        const url = new URL(window.location.href);
        Object.entries(updates).forEach(([key, value]) => {
            if (!value || value === "null" || value === "undefined") {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, value);
            }
        });
        window.history.pushState({}, "", url);
    };

    const loadVehiclesFromUrl = async () => {
        const paramsUrl = new URLSearchParams(window.location.search);
        const currentPage = Number(paramsUrl.get("page")) || 1;
        const currentSearchString = paramsUrl.get("searchString") || "";

        const query: any = {
            page: currentPage,
            limit,
            sortBy: "title",
            sortOrder: "asc",
            showAll: true,
        };

        // Always include search string if present
        if (currentSearchString) {
            query.searchString = currentSearchString;
        }

        // Always include other filters if present (let backend handle combination)
        if (paramsUrl.get("odooId")) {
            query.odooId = paramsUrl.get("odooId");
        }
        if (paramsUrl.get("defaultPrice")) {
            query.defaultPrice = paramsUrl.get("defaultPrice");
        }
        if (
            paramsUrl.get("active") !== null &&
            paramsUrl.get("active") !== ""
        ) {
            query.active = paramsUrl.get("active");
        }
        if (paramsUrl.get("notAudited") === "true") {
            query.notAudited = true;
        }

        setLoading(true);
        try {
            const result: any = await API.paginateVehicles(query);
            setVehicles(result.data);
            setPage(currentPage);
            setTotalPages(result.pagination.totalPages);
            setTotalItems(result.pagination.total);

            // Update filters for display
            setFilters({
                ...(query.searchString && { searchString: query.searchString }),
                ...(query.odooId && { odooId: query.odooId }),
                ...(query.defaultPrice && { defaultPrice: query.defaultPrice }),
                ...(query.active !== undefined && { active: query.active }),
                ...(query.notAudited && { notAudited: query.notAudited }),
            });
        } catch (error) {
            console.error("Failed to load vehicles:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = async () => {
        updateQueryParams({
            searchString,
            odooId,
            defaultPrice,
            active,
            notAudited,
            page: "1", // Reset to first page when applying filters
        });
        await loadVehiclesFromUrl();
    };

    const clearAllFilters = async () => {
        updateQueryParams({
            odooId: "",
            defaultPrice: "",
            active: "",
            notAudited: "",
            searchString: "",
            page: "1",
        });
        setOdooId("");
        setDefaultPrice("");
        setActive("");
        setNotAudited("");
        setSearchString("");
        await loadVehiclesFromUrl();
    };

    const handleSync = async () => {
        setSyncLoading(true);
        await API.syncWithOdooData();
        setSyncLoading(false);
        loadVehiclesFromUrl();
    };

    const goToPage = async (newPage: number) => {
        if (newPage < 1 || newPage > totalPages) return;

        updateQueryParams({
            page: newPage.toString(),
            searchString,
            odooId,
            defaultPrice,
            active,
            notAudited,
        });

        await loadVehiclesFromUrl();
    };

    // Debounced search handler - now applies all filters together
    useEffect(() => {
        const timer = setTimeout(() => {
            updateQueryParams({
                searchString,
                odooId,
                defaultPrice,
                active,
                notAudited,
                page: "1",
            });
            loadVehiclesFromUrl();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchString]); // Only trigger on search string change

    // Load data on initial render
    useEffect(() => {
        loadVehiclesFromUrl();
    }, []);

    const activeFiltersCount = Object.keys(filters).filter(
        (key) => filters[key] !== undefined && filters[key] !== ""
    ).length;

    return (
        <div>
            <Container style={{ marginTop: 100 }}>
                <Row>
                    <Col xs="2" className="d-flex align-items-center gap-2">
                        <a
                            className="btn btn-outline-black btn-md mx-1"
                            href={`/`}
                        >
                            <i className="bi bi-chevron-left fs-4"></i>
                        </a>
                        <h3 className="fw-medium text-primary">Vehicles</h3>
                    </Col>
                    <Col xs="10" className="col-text-end">
                        <Row className="g-2">
                            <Col
                                cols="12"
                                className="d-flex justify-content-end gap-2"
                            >
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search vehicles..."
                                    style={{ maxWidth: "250px" }}
                                    value={searchString}
                                    onChange={(e) =>
                                        setSearchString(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleApplyFilters();
                                        }
                                    }}
                                />
                                <button
                                    className="btn btn-sm btn-primary mx-1"
                                    data-bs-toggle="modal"
                                    data-bs-target="#filterModal"
                                >
                                    <i className="bi bi-funnel"></i> Filter
                                    {activeFiltersCount > 0 && (
                                        <span className="badge bg-secondary mx-2">
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    className="btn btn-sm text-white"
                                    style={{
                                        backgroundColor: "#875A7B",
                                    }}
                                    onClick={handleSync}
                                    disabled={syncLoading}
                                >
                                    <i className="bi bi-arrow-repeat mx-2"></i>
                                    {syncLoading ? "Getting Data" : "Sync Data"}
                                </button>
                                <a
                                    className="btn btn-sm text-white btn-secondary pt-2"
                                    href="/vehicle-variants-without-gallery"
                                >
                                    <i className="bi bi-image mx-2"></i>
                                    No Gallery
                                </a>
                                <a
                                    className="btn btn-sm text-white btn-secondary pt-2"
                                    href="/vehicles-without-spec"
                                >
                                    <i className="bi bi-image mx-2"></i>
                                    No Spec
                                </a>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row>
                    <Col cols="12" className="d-flex justify-content-end"></Col>
                </Row>
                <Row className="mt-5">
                    {loading ? (
                        <div
                            className="d-flex justify-content-center my-5"
                            style={{ minHeight: "100vh" }}
                        >
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>
                    ) : (
                        <table className="table mt-3">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Name</th>
                                    <th>Price</th>
                                    <th>Available For Sale</th>
                                    <th>Public</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map((vehicle, index) => (
                                    <tr key={index}>
                                        <td>
                                            <img
                                                src={
                                                    vehicle.productImage ||
                                                    "/images/no-image.png"
                                                }
                                                width={80}
                                                height={60}
                                                className="rounded-2"
                                            />
                                        </td>
                                        <td className="fw-normal">
                                            {vehicle.title}
                                        </td>
                                        <td>
                                            {vehicle.defaultPrice
                                                ? `AED ${vehicle.defaultPrice.toLocaleString()}`
                                                : "-"}
                                        </td>
                                        <td>{vehicle.validStock}</td>
                                        <td>{vehicle.active ? "Yes" : "No"}</td>
                                        <td>
                                            <div className="d-grid gap-2 d-flex flex-row">
                                                <a
                                                    href={`/vehicle/${vehicle._id}`}
                                                    className="btn btn-light mx-1 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "36px",
                                                        height: "36px",
                                                        borderRadius: "50%",
                                                    }}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Updated Pagination */}
                    <nav
                        aria-label="Page navigation"
                        style={{ marginBottom: 200 }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                Showing{" "}
                                {vehicles.length > 0
                                    ? (page - 1) * limit + 1
                                    : 0}{" "}
                                to {Math.min(page * limit, totalItems)}
                                of {totalItems} vehicles
                            </div>
                            <ul className="pagination">
                                <li
                                    className={`page-item ${
                                        page <= 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(1)}
                                        disabled={page <= 1}
                                    >
                                        First
                                    </button>
                                </li>
                                <li
                                    className={`page-item ${
                                        page <= 1 ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page - 1)}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {/* Page numbers - show up to 5 around current page */}
                                {totalPages > 0 &&
                                    Array.from(
                                        { length: Math.min(5, totalPages) },
                                        (_, i) => {
                                            let pageNum: number;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }

                                            return (
                                                <li
                                                    key={i}
                                                    className={`page-item ${
                                                        page === pageNum
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() =>
                                                            goToPage(pageNum)
                                                        }
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        }
                                    )}

                                <li
                                    className={`page-item ${
                                        page >= totalPages ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(page + 1)}
                                        disabled={page >= totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                                <li
                                    className={`page-item ${
                                        page >= totalPages ? "disabled" : ""
                                    }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() => goToPage(totalPages)}
                                        disabled={page >= totalPages}
                                    >
                                        Last
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </Row>
            </Container>

            {/* Filter Modal */}
            <div
                className="modal fade"
                id="filterModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Filter Vehicles</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-info">
                                <small>
                                    <i className="bi bi-info-circle me-2"></i>
                                    You can combine search with other filters
                                    for more precise results.
                                </small>
                            </div>
                            <Col className="mt-3">
                                <label className="form-label">Search</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search vehicles..."
                                    value={searchString}
                                    onChange={(e) =>
                                        setSearchString(e.target.value)
                                    }
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">Odoo Id</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter odooId"
                                    value={odooId}
                                    onChange={(e) => setOdooId(e.target.value)}
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">Price</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter price"
                                    value={defaultPrice}
                                    onChange={(e) =>
                                        setDefaultPrice(e.target.value)
                                    }
                                />
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">Active</label>
                                <select
                                    className="form-select"
                                    value={active}
                                    onChange={(e) => setActive(e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="true">Public</option>
                                    <option value="false">Hidden</option>
                                </select>
                            </Col>
                            <Col className="mt-3">
                                <label className="form-label">Audit Status</label>
                                <select
                                    className="form-select"
                                    value={notAudited}
                                    onChange={(e) => setNotAudited(e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="true">Not Audited</option>
                                </select>
                            </Col>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary text-white"
                                data-bs-dismiss="modal"
                                onClick={clearAllFilters}
                            >
                                <i className="bi bi-x mx-2"></i> Clear All
                                Filters
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={handleApplyFilters}
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Vehicles;
